import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/axios';
import GlobalPagination from '../ui/GlobalPagination';

// ── ATS config ────────────────────────────────────────────────────────────────

const ATS_CONFIG = {
    workday: {
        label:    'Workday',
        apiBase:  '/workday',
        gradient: 'linear-gradient(135deg,#0041C2,#1A5FD4)',
        accent:   '#0041C2',
        accentBg: 'rgba(0,65,194,0.12)',
        accentBorder: 'rgba(0,65,194,0.2)',
        accentText: '#1A5FD4',
        accentBannerBg: 'rgba(0,65,194,0.07)',
        accentBannerBorder: 'rgba(0,65,194,0.2)',
    },
    greenhouse: {
        label:    'Greenhouse',
        apiBase:  '/greenhouse',
        gradient: 'linear-gradient(135deg,#1a8a3c,#24a84b)',
        accent:   '#1a8a3c',
        accentBg: 'rgba(26,138,60,0.12)',
        accentBorder: 'rgba(26,138,60,0.2)',
        accentText: '#1a8a3c',
        accentBannerBg: 'rgba(26,138,60,0.07)',
        accentBannerBorder: 'rgba(26,138,60,0.25)',
    },
};

// ── Mock applicant generator ──────────────────────────────────────────────────

const CANDIDATE_POOL = [
    { first: 'Arjun',    last: 'Mehta',    domain: 'gmail.com',   phone: '+1-408-312-5784' },
    { first: 'Priya',    last: 'Sharma',   domain: 'outlook.com', phone: '+1-650-229-4471' },
    { first: 'James',    last: 'Nguyen',   domain: 'gmail.com',   phone: '+1-512-674-8823' },
    { first: 'Sarah',    last: 'Mitchell', domain: 'yahoo.com',   phone: '+1-415-388-9012' },
    { first: 'Rahul',    last: 'Verma',    domain: 'gmail.com',   phone: '+1-669-217-3345' },
    { first: 'Emily',    last: 'Johnson',  domain: 'outlook.com', phone: '+1-206-541-7762' },
    { first: 'Kiran',    last: 'Patel',    domain: 'gmail.com',   phone: '+1-925-448-0092' },
    { first: 'Michael',  last: 'Torres',   domain: 'hotmail.com', phone: '+1-737-211-6603' },
    { first: 'Anjali',   last: 'Singh',    domain: 'gmail.com',   phone: '+1-469-878-5534' },
    { first: 'David',    last: 'Kim',      domain: 'gmail.com',   phone: '+1-626-330-1187' },
    { first: 'Meena',    last: 'Rajput',   domain: 'outlook.com', phone: '+1-313-572-4421' },
    { first: 'Chris',    last: 'Anderson', domain: 'gmail.com',   phone: '+1-214-693-8850' },
    { first: 'Neha',     last: 'Gupta',    domain: 'gmail.com',   phone: '+1-408-912-3367' },
    { first: 'Brandon',  last: 'Williams', domain: 'yahoo.com',   phone: '+1-704-331-9940' },
    { first: 'Sanjay',   last: 'Iyer',     domain: 'gmail.com',   phone: '+1-510-248-7723' },
    { first: 'Lauren',   last: 'Clark',    domain: 'outlook.com', phone: '+1-602-187-5564' },
    { first: 'Vikram',   last: 'Nair',     domain: 'gmail.com',   phone: '+1-972-563-2218' },
    { first: 'Jessica',  last: 'Brown',    domain: 'gmail.com',   phone: '+1-347-809-4453' },
    { first: 'Aditya',   last: 'Kumar',    domain: 'hotmail.com', phone: '+1-503-724-9981' },
    { first: 'Hannah',   last: 'Lee',      domain: 'gmail.com',   phone: '+1-253-614-0076' },
    { first: 'Ravi',     last: 'Chandran', domain: 'gmail.com',   phone: '+1-408-773-4452' },
    { first: 'Tyler',    last: 'Moore',    domain: 'outlook.com', phone: '+1-615-883-6691' },
    { first: 'Deepa',    last: 'Menon',    domain: 'gmail.com',   phone: '+1-650-441-2287' },
    { first: 'Nathan',   last: 'Harris',   domain: 'yahoo.com',   phone: '+1-480-329-5543' },
    { first: 'Smita',    last: 'Joshi',    domain: 'gmail.com',   phone: '+1-925-667-1120' },
    { first: 'Alex',     last: 'Martinez', domain: 'gmail.com',   phone: '+1-916-228-8874' },
    { first: 'Pooja',    last: 'Rao',      domain: 'outlook.com', phone: '+1-512-997-3346' },
    { first: 'Kevin',    last: 'Wilson',   domain: 'gmail.com',   phone: '+1-469-554-0023' },
    { first: 'Divya',    last: 'Pillai',   domain: 'gmail.com',   phone: '+1-408-215-8897' },
    { first: 'Marcus',   last: 'Davis',    domain: 'hotmail.com', phone: '+1-617-432-7712' },
];

const EXP_LEVELS = [2, 3, 4, 5, 6, 7, 8, 10, 12];

function getSummaryTemplate(title = '', exp) {
    const t = title.toLowerCase();
    if (/data scientist|machine learning|ml engineer|ai engineer/.test(t))
        return `${exp}+ yrs in data science & ML. Proficient in Python, TensorFlow, PyTorch, scikit-learn. Built predictive models improving KPIs by 30%.`;
    if (/data analyst|business analyst|bi developer/.test(t))
        return `${exp}+ yrs in data analysis. Expert in SQL, Power BI, Tableau. Delivered insights driving $2M in cost savings.`;
    if (/devops|site reliability|sre|cloud engineer|platform engineer/.test(t))
        return `${exp}+ yrs in DevOps & cloud. Expert in AWS/GCP/Azure, Kubernetes, Docker, Terraform, CI/CD pipeline design.`;
    if (/frontend|react|angular|vue|ui developer/.test(t))
        return `${exp}+ yrs frontend dev. Strong in React, TypeScript, CSS. Built accessible web apps serving 1M+ users.`;
    if (/backend|java|spring|node|python developer/.test(t))
        return `${exp}+ yrs backend engineering. Java/Spring Boot, REST APIs, microservices, PostgreSQL, Redis.`;
    if (/full.?stack|full stack/.test(t))
        return `${exp}+ yrs full-stack. React + Node.js/Java Spring Boot. End-to-end features from DB schema to responsive UI.`;
    if (/software engineer|software developer|sde|swe/.test(t))
        return `${exp}+ yrs software engineering. Java, Python, distributed systems, Agile. High-availability services at scale.`;
    if (/product manager|pm |product owner/.test(t))
        return `${exp}+ yrs product management. Led 0-to-1 launches, cross-functional roadmaps, A/B testing. +25% user retention.`;
    if (/qa|quality assurance|test engineer|sdet/.test(t))
        return `${exp}+ yrs QA engineering. Selenium, Cypress, JUnit, API testing. 95% test coverage on critical services.`;
    if (/security|cybersecurity|infosec/.test(t))
        return `${exp}+ yrs security engineering. CISSP certified. Pen testing, SIEM tools, zero-trust architecture.`;
    if (/sales|account executive|account manager/.test(t))
        return `${exp}+ yrs B2B SaaS sales. Consistent 120%+ quota attainment. $5M+ pipeline. Salesforce CRM proficient.`;
    if (/hr|human resources|recruiter|talent/.test(t))
        return `${exp}+ yrs HR & talent acquisition. Reduced time-to-hire by 35%, scaled teams from 20 to 120+.`;
    if (/finance|financial analyst|accounting/.test(t))
        return `${exp}+ yrs finance. CPA certified. FP&A, variance analysis, SAP/Oracle ERP. Led $50M budget cycle.`;
    if (/marketing|growth|seo|content/.test(t))
        return `${exp}+ yrs digital marketing. 3x organic traffic growth via SEO. $2M paid media budget. HubSpot certified.`;
    return `${exp}+ yrs professional experience in ${title}. Proven track record delivering impactful results in fast-paced environments.`;
}

function seededShuffle(arr, seed) {
    const a = [...arr];
    let s = seed;
    for (let i = a.length - 1; i > 0; i--) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const j = s % (i + 1);
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function generateMockApplicants(job, atsLabel) {
    const seed = job.id ? job.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) : 42;
    const shuffled    = seededShuffle(CANDIDATE_POOL, seed);
    const expShuffled = seededShuffle(EXP_LEVELS, seed + 7);
    const count = 6 + (seed % 5);
    return shuffled.slice(0, count).map((c, i) => {
        const exp = expShuffled[i % expShuffled.length];
        return {
            resumeId: `${atsLabel.toLowerCase()}-mock-${seed}-${i}`,
            candidateName: `${c.first} ${c.last}`,
            email: `${c.first.toLowerCase()}.${c.last.toLowerCase()}@${c.domain}`,
            mobile: c.phone,
            experience: exp,
            summary: getSummaryTemplate(job.title, exp),
            appliedDate: new Date(Date.now() - (i * 2 + 1) * 86400000 * 3)
                .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            source: atsLabel.toLowerCase(),
        };
    });
}

// ── Main component ────────────────────────────────────────────────────────────

const ATSImportModal = ({ show, onHide, onImportResumes, positionId, atsType = 'workday' }) => {
    const config = ATS_CONFIG[atsType] || ATS_CONFIG.workday;

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Step 1 — job list
    const [jobs, setJobs] = useState([]);
    const [jobSearch, setJobSearch] = useState('');
    const [jobPage, setJobPage] = useState(1);
    const [jobTotalPages, setJobTotalPages] = useState(1);
    const [jobTotal, setJobTotal] = useState(0);
    const [companies, setCompanies] = useState([]);
    const [companyFilter, setCompanyFilter] = useState('all');
    const [selectedJob, setSelectedJob] = useState(null);
    const jobSearchTimer = useRef(null);

    // Step 2 — applicants
    const [applicants, setApplicants] = useState([]);
    const [applicantSearch, setApplicantSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        if (show) {
            setStep(1);
            setError(null);
            setJobs([]);
            setJobSearch('');
            setJobPage(1);
            setCompanyFilter('all');
            setSelectedJob(null);
            setApplicants([]);
            setApplicantSearch('');
            setSelectedIds([]);
            loadCompanies();
            loadJobs(1, '', 'all');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [show, atsType]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Loaders ───────────────────────────────────────────────────────────────

    const loadCompanies = async () => {
        try {
            const r = await api.get(`${config.apiBase}/companies`);
            if (r.data?.success) setCompanies(r.data.data || []);
        } catch {}
    };

    const loadJobs = async (page, search, company) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: page || 1, search: search || '' });
            if (company && company !== 'all') params.set('company', company);
            const r = await api.get(`${config.apiBase}/jobs?${params.toString()}`);
            if (r.data?.success) {
                setJobs(r.data.data || []);
                setJobTotal(r.data.totalCount || 0);
                setJobTotalPages(Math.max(1, r.data.totalPages || 1));
                setJobPage(page || 1);
            } else {
                setError(r.data?.error || `Failed to load ${config.label} jobs`);
            }
        } catch (err) {
            setError(`Failed to load ${config.label} jobs: ` + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleJobSearchChange = (val) => {
        setJobSearch(val);
        clearTimeout(jobSearchTimer.current);
        jobSearchTimer.current = setTimeout(() => loadJobs(1, val, companyFilter), 350);
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
        setApplicantSearch('');
        setSelectedIds([]);
        setApplicants(generateMockApplicants(job, config.label));
        setStep(2);
    };

    const filteredApplicants = () => {
        if (!applicantSearch.trim()) return applicants;
        const q = applicantSearch.toLowerCase();
        return applicants.filter(a =>
            (a.candidateName || '').toLowerCase().includes(q) ||
            (a.email || '').toLowerCase().includes(q) ||
            (a.mobile || '').toLowerCase().includes(q) ||
            (a.summary || '').toLowerCase().includes(q)
        );
    };

    const toggleApplicant = (id) =>
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const toggleAll = () => {
        const filtered = filteredApplicants();
        const allIds = filtered.map(a => a.resumeId);
        const allSel = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
        setSelectedIds(allSel ? [] : allIds);
    };

    const handleImport = () => {
        const fa = filteredApplicants();
        const toImport = fa.filter(a => selectedIds.includes(a.resumeId));
        if (toImport.length === 0) { setError('Please select at least one applicant to import.'); return; }
        setImporting(true);
        const mapped = toImport.map(a => ({
            resumeId:      a.resumeId,
            candidateName: a.candidateName,
            email:         a.email,
            mobile:        a.mobile,
            fileName:      `${a.candidateName.replace(/\s+/g, '_')}_resume.pdf`,
            fileUrl:       null,
            fileSize:      0,
            atsJobId:      selectedJob?.id,
            atsJobTitle:   selectedJob?.title,
            atsSource:     atsType,
            importedFromAts: true,
        }));
        onImportResumes(mapped);
        setImporting(false);
        handleClose();
    };

    const handleClose = () => {
        setStep(1);
        setSelectedJob(null);
        setSelectedIds([]);
        setError(null);
        onHide();
    };

    if (!show) return null;

    // ── Styles ────────────────────────────────────────────────────────────────

    const thStyle = {
        padding: '8px 10px', fontSize: 11, fontWeight: 700,
        color: 'var(--ds-text2)', background: 'var(--ds-card2)',
        borderBottom: '1px solid var(--ds-border)',
        position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap',
    };
    const tdStyle = {
        padding: '7px 10px', fontSize: 12, color: 'var(--ds-text)',
        borderBottom: '1px solid var(--ds-table-border)', verticalAlign: 'middle',
    };
    const footBtn = (grad) => ({
        padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 8,
        border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        background: grad, color: '#fff', transition: 'all 0.18s', outline: 'none',
        display: 'inline-flex', alignItems: 'center', gap: 6,
    });

    const fa = filteredApplicants();
    const allIds = fa.map(a => a.resumeId);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 12px', paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 12px)' }}>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={handleClose} />

            <div style={{
                position: 'relative', width: '100%', maxWidth: 1000,
                background: 'var(--ds-card)', borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                display: 'flex', flexDirection: 'column',
                maxHeight: 'calc(100vh - 48px)', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{ padding: '12px 16px', background: config.gradient, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fas fa-cloud-download-alt" style={{ color: '#fff', fontSize: 13 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>Import Resumes from {config.label}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                                {step === 1
                                    ? `Step 1 of 2 — Select a ${config.label} job posting`
                                    : `Step 2 of 2 — ${config.label} applicants for "${selectedJob?.title}"`}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {step === 2 && (
                            <button
                                onClick={() => { setStep(1); setError(null); }}
                                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', outline: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                                <i className="fas fa-arrow-left" style={{ fontSize: 11 }} /> Back
                            </button>
                        )}
                        <button onClick={handleClose} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none' }}>×</button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--ds-input-bg)', display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {error && (
                        <div style={{ padding: '10px 14px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <i className="fas fa-exclamation-circle" style={{ color: '#dc2626', fontSize: 14, flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 12, color: '#dc2626', flex: 1 }}>{error}</span>
                            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                        </div>
                    )}

                    {loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                            <div className="spinner-border" style={{ color: config.accent, width: 32, height: 32 }} role="status" />
                            <p style={{ marginTop: 12, color: 'var(--ds-text2)', fontSize: 13, marginBottom: 0 }}>Loading…</p>
                        </div>
                    )}

                    {/* ── Step 1: pick job ── */}
                    {step === 1 && !loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type="text"
                                    placeholder={`Search ${config.label} jobs by title, location, department…`}
                                    value={jobSearch}
                                    onChange={e => handleJobSearchChange(e.target.value)}
                                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-card)', color: 'var(--ds-text)', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
                                />
                                {companies.length > 0 && (
                                    <select
                                        value={companyFilter}
                                        onChange={e => { const c = e.target.value; setCompanyFilter(c); loadJobs(1, jobSearch, c); }}
                                        style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-card)', color: 'var(--ds-text)', fontSize: 12, fontFamily: 'inherit', outline: 'none', minWidth: 150 }}
                                    >
                                        <option value="all">All Companies</option>
                                        {companies.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                    </select>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)' }}>Select {config.label} Job Posting</span>
                                <span style={{ padding: '3px 10px', borderRadius: 6, background: config.accentBg, color: config.accentText, fontSize: 11, fontWeight: 600, border: `1px solid ${config.accentBorder}` }}>
                                    {jobTotal} jobs
                                </span>
                            </div>

                            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'auto', maxHeight: 340, background: 'var(--ds-card)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: 40 }}>Select</th>
                                            <th style={thStyle}>Job Title</th>
                                            <th style={thStyle}>Company</th>
                                            <th style={thStyle}>Location</th>
                                            <th style={thStyle}>Posted</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {jobs.length > 0 ? jobs.map(job => (
                                            <tr key={job.id} onClick={() => handleSelectJob(job)} style={{ cursor: 'pointer' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-table-hover)'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <input type="radio" name="atsJobSel"
                                                        checked={selectedJob?.id === job.id}
                                                        onChange={() => handleSelectJob(job)}
                                                        style={{ accentColor: config.accent, cursor: 'pointer' }}
                                                        onClick={e => e.stopPropagation()} />
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: 600 }}>{job.title || '—'}</div>
                                                    {job.department && <div style={{ fontSize: 11, color: 'var(--ds-text2)', marginTop: 2 }}>{job.department}</div>}
                                                </td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)' }}>{job.company || '—'}</td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)' }}>{job.location || '—'}</td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)', whiteSpace: 'nowrap' }}>{job.postedOn || '—'}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', padding: 32, color: 'var(--ds-text2)' }}>
                                                    {jobSearch ? 'No jobs found matching your search.' : `No ${config.label} jobs available.`}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {jobTotalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <GlobalPagination
                                        currentPage={jobPage}
                                        totalPages={jobTotalPages}
                                        onPageChange={p => loadJobs(p, jobSearch, companyFilter)}
                                        totalItems={jobTotal}
                                        startIndex={jobTotal > 0 ? (jobPage - 1) * 20 + 1 : 0}
                                        endIndex={Math.min(jobPage * 20, jobTotal)}
                                        label="jobs"
                                        size="medium"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 2: pick applicants ── */}
                    {step === 2 && !loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {/* Selected job banner */}
                            <div style={{ padding: '8px 12px', background: config.accentBannerBg, border: `1px solid ${config.accentBannerBorder}`, borderRadius: 8, fontSize: 12 }}>
                                <span style={{ color: config.accentText, fontWeight: 700 }}>{config.label} Job: </span>
                                <span style={{ color: 'var(--ds-text)', fontWeight: 600 }}>{selectedJob?.title}</span>
                                {selectedJob?.company && <span style={{ color: 'var(--ds-text2)', marginLeft: 8 }}>· {selectedJob.company}</span>}
                                {selectedJob?.location && <span style={{ color: 'var(--ds-text2)', marginLeft: 8 }}>· {selectedJob.location}</span>}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)' }}>Applicants from {config.label}</span>
                                <span style={{ padding: '3px 10px', borderRadius: 6, background: config.accentBg, color: config.accentText, fontSize: 11, fontWeight: 600, border: `1px solid ${config.accentBorder}` }}>
                                    {fa.length} applicant{fa.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, summary…"
                                    value={applicantSearch}
                                    onChange={e => setApplicantSearch(e.target.value)}
                                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-card)', color: 'var(--ds-text)', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
                                />
                                <button onClick={toggleAll} disabled={fa.length === 0}
                                    style={{ ...footBtn(config.gradient), opacity: fa.length === 0 ? 0.5 : 1, padding: '7px 12px', fontSize: 11 }}>
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'auto', maxHeight: 320, background: 'var(--ds-card)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: 40 }}>
                                                <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={fa.length === 0}
                                                    style={{ accentColor: config.accent, cursor: fa.length === 0 ? 'not-allowed' : 'pointer' }} />
                                            </th>
                                            <th style={thStyle}>Candidate</th>
                                            <th style={thStyle}>Exp</th>
                                            <th style={thStyle}>Contact</th>
                                            <th style={thStyle}>Profile Summary</th>
                                            <th style={thStyle}>Applied</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fa.length > 0 ? fa.map(a => {
                                            const checked = selectedIds.includes(a.resumeId);
                                            return (
                                                <tr key={a.resumeId} onClick={() => toggleApplicant(a.resumeId)}
                                                    style={{ cursor: 'pointer', background: checked ? `${config.accentBg}` : '' }}
                                                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'var(--ds-table-hover)'; }}
                                                    onMouseLeave={e => { if (!checked) e.currentTarget.style.background = ''; }}>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleApplicant(a.resumeId)}
                                                            onClick={e => e.stopPropagation()}
                                                            style={{ accentColor: config.accent, cursor: 'pointer' }} />
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <strong style={{ display: 'block' }}>{a.candidateName}</strong>
                                                        <span style={{ fontSize: 10, color: config.accentText, background: config.accentBg, padding: '1px 6px', borderRadius: 4, marginTop: 2, display: 'inline-block' }}>{config.label}</span>
                                                    </td>
                                                    <td style={{ ...tdStyle, color: 'var(--ds-text2)', whiteSpace: 'nowrap' }}>{a.experience} yrs</td>
                                                    <td style={{ ...tdStyle, color: 'var(--ds-text2)', fontSize: 11 }}>
                                                        <div>{a.email}</div>
                                                        <div style={{ marginTop: 2 }}>{a.mobile}</div>
                                                    </td>
                                                    <td style={{ ...tdStyle, color: 'var(--ds-text2)', maxWidth: 280, fontSize: 11 }}>{a.summary}</td>
                                                    <td style={{ ...tdStyle, color: 'var(--ds-text2)', whiteSpace: 'nowrap', fontSize: 11 }}>{a.appliedDate}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: 32, color: 'var(--ds-text2)' }}>
                                                    {applicantSearch ? 'No applicants match your search.' : 'No applicants found.'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {selectedIds.length > 0 && (
                                <div style={{ fontSize: 12, color: config.accentText, fontWeight: 600 }}>
                                    {selectedIds.length} applicant{selectedIds.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '10px 16px', background: 'var(--ds-card)', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <button onClick={handleClose} style={{ ...footBtn('var(--ds-surface)'), color: 'var(--ds-text2)', border: '1px solid var(--ds-border)' }}>Cancel</button>
                    {step === 2 && (
                        <button onClick={handleImport} disabled={importing || selectedIds.length === 0}
                            style={{ ...footBtn(config.gradient), opacity: selectedIds.length === 0 ? 0.5 : 1 }}>
                            {importing
                                ? <><span className="spinner-border spinner-border-sm me-2" />Importing…</>
                                : <><i className="fas fa-download" style={{ marginRight: 6 }} />Import {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}Applicant{selectedIds.length !== 1 ? 's' : ''}</>
                            }
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ATSImportModal;
